import { useEffect, useState, useRef } from "react";
import { io } from "socket.io-client";
import "./App.css";

function App() {
  const socketRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const imagePasteRef = useRef(null);
  const bufferRef = useRef([]);        // временный буфер для приходящих сообщений до initial load
  const initialLoadedRef = useRef(false);

// helper: timestamp из _id (ms)
function idToTimestampMs(id) {
  try {
    return parseInt(String(id).substring(0, 8), 16) * 1000;
  } catch {
    return 0;
  }
}

// helper: объединить, дедуплицировать по _id и отсортировать по времени (возрастание)
function mergeAndSortMessages(existing = [], incoming = []) {
  const map = new Map();
  for (const m of existing) if (m && m._id) map.set(String(m._id), m);
  for (const m of incoming) if (m && m._id) map.set(String(m._id), m);
  const arr = Array.from(map.values());
  arr.sort((a, b) => idToTimestampMs(a._id) - idToTimestampMs(b._id));
  return arr;
}


  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [messages]);


  // App.js (фрагмент — замените существующий useEffect и handlePasteImage)
  // Замените ваш useEffect на этот
  useEffect(() => {
  socketRef.current = io("https://hh.meta-void.ru", {
    path: "/chat/socket.io",
    transports: ["websocket", "polling"],
    withCredentials: true,
    reconnection: true,
  });

  const socket = socketRef.current;

  // Обработчик приходящих одиночных сообщений (реaltime)
  const onGetMessage = (msg) => {
    // Если initial load ещё не завершён — буферизуем
    if (!initialLoadedRef.current) {
      bufferRef.current.push(msg);
      return;
    }
    // Иначе просто добавляем и сортируем
    setMessages((prev) => mergeAndSortMessages(prev, [msg]));
  };

  // Обработчик для полной загрузки из БД
  const onNewMessages = (msgs) => {
    // msgs — массив из БД
    // Объединяем с тем, что накопилось в буфере
    const buffered = bufferRef.current.splice(0); // взять и очистить буфер
    const merged = mergeAndSortMessages(msgs || [], buffered || []);
    setMessages(merged);
    initialLoadedRef.current = true;
  };

  const onConnect = () => {
    console.log("socket connected", socket.id);
    // Запрашиваем все сообщения; пока ждём — onGetMessage будет буферизовать
    socket.emit("getAllMessages");
  };

  const onConnectError = (err) => console.warn("socket connect_error", err);

  // Подписываемся
  socket.on("connect", onConnect);
  socket.on("newMessages", onNewMessages);
  socket.on("getMessage", onGetMessage);
  socket.on("connect_error", onConnectError);

  // Cleanup
  return () => {
    if (socketRef.current) {
      socketRef.current.off("connect", onConnect);
      socketRef.current.off("newMessages", onNewMessages);
      socketRef.current.off("getMessage", onGetMessage);
      socketRef.current.off("connect_error", onConnectError);
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  };
}, []);



  const sendMessage = () => {
    const socket = socketRef.current;
    if (!socket) return;
    if (!text.trim()) return;
    socket.emit("newMessage", { text, senderid: socket.id });
    setText("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handlePasteImage = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        const reader = new FileReader();

        // используем событие load и берем ev.target.result (ArrayBuffer)
        reader.onload = (ev) => {
          const arrayBuffer = ev.target.result;
          const socket = socketRef.current;
          if (!socket) return;

          // отправляем бинарный ArrayBuffer — socket.io поддерживает бинарные данные
          socket.emit("uploadImage", {
            buffer: arrayBuffer,
            imageName: `pasted_${Date.now()}.png`,
            senderid: socket.id,
          });
        };

        reader.readAsArrayBuffer(file);
        e.preventDefault();
        if (e.target) e.target.value = "";
      }
    }
  };

  const focusImageInput = () => {
    imagePasteRef.current?.focus();
  };

  return (
    <div className="App">
      <div className="main">
        <div className="left">
          <form onSubmit={(e) => e.preventDefault()}>
            <textarea
              rows="5"
              cols="35"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Введите сообщение..."
            />
          </form>

          <form onSubmit={(e) => e.preventDefault()}>
            <label
              htmlFor="imagePasteArea"
              onClick={focusImageInput}
              style={{
                display: "block",
                marginTop: "1em",
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              Для изображений (нажми сюда и вставь Ctrl+V)
            </label>
            <textarea
              id="imagePasteArea"
              ref={imagePasteRef}
              rows="2"
              cols="35"
              onPaste={handlePasteImage}
              placeholder="Вставьте изображение из буфера..."
            />
          </form>
        </div>

        <div className="right">
          <h3>Сообщения</h3>
          <div
            className="messages-container"
            ref={messagesContainerRef}
            style={{ overflowY: "auto", maxHeight: "60vh" }}
          >
            {messages.map((msg, index) => {
              const senderId = socketRef.current ? socketRef.current.id : null;
              const key = msg._id || index;
              return (
                <div
                  key={key}
                  className={`message ${
                    msg.senderid === senderId ? "my" : "other"
                  }`}
                >
                  {msg.text === "image" && msg.imgUrl && (
                    <a
                      href={msg.imgUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {msg.imgUrl}
                    </a>
                  )}
                  {msg.text !== "image" && (
                    <span dangerouslySetInnerHTML={{ __html: msg.text }} />
                  )}
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;

